import React, { useState, useEffect, useMemo } from 'react';
import { FiMenu, FiLogOut, FiUser } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import LanguageSelector from '.././common/LanguageSelector/LanguageSelector';
import styles from './ClientHeader.module.css';

interface ClientHeaderProps {
  onMenuToggle: () => void;
}

const ClockDisplay: React.FC = () => {
  const [time, setTime] = useState('');
  const { i18n } = useTranslation();

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const langCode = i18n.language.split('-')[0];
      setTime(
        now.toLocaleTimeString(langCode === 'es' ? 'es-MX' : 'en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      );
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [i18n.language]);

  return <span className={styles.clock}>{time}</span>;
};

const ClientHeader: React.FC<ClientHeaderProps> = ({ onMenuToggle }) => {
  const { user, logout, profileComplete } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const firstName = useMemo(
    () => user?.name?.split(' ')[0] || t('profile.user_role_client'),
    [user?.name, t]
  );

  const roleLabel = useMemo(() => {
    if (user?.role === 'developer') return t('profile.user_role_developer');
    if (user?.role === 'manager') return t('profile.user_role_manager');
    return t('profile.user_role_client');
  }, [user?.role, t]);

  const showProfileCta =
    user?.role === 'client' && !profileComplete && location.pathname !== '/perfil';

  return (
    <div className={styles.stickyWrapper}>
      <header className={styles.header}>
        <div className={styles.leftGroup}>
          <button
            onClick={onMenuToggle}
            aria-label={t('common.toggleMenu')}
            className={styles.menuButton}
            type="button"
          >
            <FiMenu />
          </button>
          
        </div>

        <div className={styles.rightGroup}>
          <LanguageSelector />

          <div className={styles.userInfo}>
            <span className={styles.greeting}>{t('layout.welcome', { name: firstName })}</span>
            <span className={styles.role}>{roleLabel}</span>
          </div>

          <div className={styles.avatar}>
            <FiUser />
          </div>

          <button
            onClick={() => logout()}
            title={t('common.logout')}
            aria-label={t('common.logout')}
            className={styles.logout}
            type="button"
          >
            <FiLogOut />
          </button>
        </div>
      </header>

      {showProfileCta && (
        <div className={styles.profileCta} role="alert">
          <div className={styles.profileCtaText}>{t('profile.setup_subtitle')}</div>
          <button
            type="button"
            onClick={() => navigate('/perfil')}
            className={styles.profileCtaButton}
          >
            {t('profile.setup_title')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientHeader;
